import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AlertTriangle, FileX, TriangleAlert, Loader2, RefreshCw, Search, ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SendDeliverable } from '../components/SendDeliverable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABEL = {
  parse_failure:   { text: 'Parse failure',    ink: '#B06A2C', Icon: FileX },
  verdict_missing: { text: 'Missing verdict',  ink: '#6b3a2c', Icon: AlertTriangle },
  verdict_short:   { text: 'Short verdict',    ink: '#B06A2C', Icon: TriangleAlert },
  low_confidence:  { text: 'Low confidence',   ink: '#8B8377', Icon: TriangleAlert },
};

const CHANNEL_LABEL = {
  pos_cash: 'Cash', pos_card: 'Card', pos_upi: 'UPI',
  agg_swiggy: 'Swiggy', agg_zomato: 'Zomato',
};

const fmtRs = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const fmtWhen = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

export const Exceptions = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sinceDays, setSinceDays] = useState(14);
  const [kindFilter, setKindFilter] = useState('all');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/admin/exceptions`, {
        params: { since_days: sinceDays, limit: 500 },
      });
      setRows(data.exceptions || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load exceptions');
    } finally {
      setLoading(false);
    }
  }, [sinceDays]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (kindFilter !== 'all' && r.kind !== kindFilter) return false;
    if (q) {
      const needle = q.toLowerCase();
      const hay = `${r.client_id || ''} ${r.channel || ''} ${r.message || ''}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  const kindCounts = rows.reduce((acc, r) => {
    acc[r.kind] = (acc[r.kind] || 0) + 1;
    return acc;
  }, {});

  const totalRupees = filtered.reduce((s, r) => s + (r.rupees || 0), 0);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="exceptions-page">
      <div className="mb-6">
        <div className="text-[11px] tracking-[.18em] text-[#6E5334] uppercase">Operator inbox</div>
        <h1 className="text-3xl font-semibold text-[#211C16] mt-1" style={{ fontFamily: 'Fraunces, serif' }}>
          Exceptions
        </h1>
        <p className="text-sm text-text-secondary mt-2 max-w-2xl">
          Every parse failure, low-confidence read, and short or missing verdict across every client — sorted by urgency (severity × recency, ties broken by rupees). Nothing else surfaces here; everything that does is real work.
        </p>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        {[
          ['all', `All (${rows.length})`],
          ['parse_failure',   `Parse failures (${kindCounts.parse_failure || 0})`],
          ['verdict_missing', `Missing (${kindCounts.verdict_missing || 0})`],
          ['verdict_short',   `Short (${kindCounts.verdict_short || 0})`],
          ['low_confidence',  `Low-confidence (${kindCounts.low_confidence || 0})`],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setKindFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs border transition ${
              kindFilter === k
                ? 'bg-[#2F5D48] text-[#F4F0E6] border-[#2F5D48]'
                : 'bg-white text-[#6E5334] border-[#EDE5D3] hover:border-[#B06A2C]'
            }`}
            data-testid={`exceptions-filter-${k}`}
          >{label}</button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search client / channel / message"
              className="pl-7 pr-2 py-1.5 border border-border rounded-md text-xs w-64"
              data-testid="exceptions-search"
            />
          </div>
          <select
            value={sinceDays}
            onChange={(e) => setSinceDays(Number(e.target.value))}
            className="border border-border rounded-md px-2 py-1.5 text-xs"
            data-testid="exceptions-window"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="exceptions-refresh">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-state-error bg-red-50 border border-red-200 rounded p-2 mb-3">
          {error}
        </div>
      )}

      {/* Totals band */}
      <div className="flex items-center gap-6 mb-4 text-sm">
        <div><span className="text-text-muted">Showing</span> <span className="font-semibold">{filtered.length}</span> item{filtered.length === 1 ? '' : 's'}</div>
        <div><span className="text-text-muted">Rupee impact</span> <span className="font-semibold text-[#B06A2C] font-mono">{fmtRs(totalRupees)}</span></div>
      </div>

      {/* Table */}
      <div className="bg-white border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="exceptions-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[.12em] text-[#6E5334] border-b border-[#EDE5D3]">
              <th className="py-2 px-3">Type</th>
              <th className="py-2 px-3">Client</th>
              <th className="py-2 px-3">Channel</th>
              <th className="py-2 px-3">Business date</th>
              <th className="py-2 px-3">Message</th>
              <th className="py-2 px-3 text-right">Rupees</th>
              <th className="py-2 px-3">When</th>
              <th className="py-2 px-3"></th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && filtered.length === 0 ? (
              <tr><td colSpan={9} className="py-6 text-center text-text-muted"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} className="py-8 text-center text-text-muted italic" data-testid="exceptions-empty">
                Nothing to review — every parse succeeded, every verdict matched, no missing sources. Enjoy the quiet.
              </td></tr>
            ) : filtered.map((r, i) => {
              const meta = KIND_LABEL[r.kind] || { text: r.kind, ink: '#6E5334', Icon: TriangleAlert };
              const Icon = meta.Icon;
              const drillTarget = (r.business_date && r.client_id &&
                (String(r.channel || '').startsWith('pos_') ||
                 String(r.channel || '').startsWith('agg_')))
                ? `/daily-recon?client=${encodeURIComponent(r.client_id)}&date=${r.business_date}&channel=${r.channel}`
                : null;
              return (
                <tr key={`${r.kind}-${i}`} className="border-b border-[#EDE5D3] hover:bg-[#FBF8F0]" data-testid={`exceptions-row-${i}`}>
                  <td className="py-2 px-3">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: meta.ink }}>
                      <Icon className="h-3.5 w-3.5" />{meta.text}
                    </span>
                  </td>
                  <td className="py-2 px-3 font-mono text-xs">{r.client_id || '—'}</td>
                  <td className="py-2 px-3 text-xs">{CHANNEL_LABEL[r.channel] || r.channel || '—'}</td>
                  <td className="py-2 px-3 text-xs font-mono">{r.business_date || '—'}</td>
                  <td className="py-2 px-3 text-xs text-text-secondary max-w-md truncate" title={r.message}>{r.message || '—'}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {r.rupees ? fmtRs(r.rupees) : '—'}
                  </td>
                  <td className="py-2 px-3 text-xs text-text-muted">{fmtWhen(r.occurred_at)}</td>
                  <td className="py-2 px-3">
                    {drillTarget && (
                      <Link to={drillTarget} className="text-xs text-[#2F5D48] hover:text-[#B06A2C] inline-flex items-center gap-1" data-testid={`exceptions-drill-${i}`}>
                        Drill <ExternalLink className="h-3 w-3" />
                      </Link>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {r.business_date && r.client_id && (
                      <SendDeliverable
                        clientId={r.client_id}
                        clientName={r.client_id}
                        businessDate={r.business_date}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Exceptions;
